// slices/mappingJobsSlice/mappingJobsSlice.ts
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import { logout } from '../auth/authSlice';
import { Vignette } from '../../types/Vignette';
import { Flashcard } from '../../types/Flashcard';
import { Video } from '../../types/Video';
import type { DocumentModelPhase } from '../../types/documentModel';

type MappingJobsStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export enum JobStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  TIMED_OUT = 'TIMED_OUT',
}

export enum JobStage {
  PARSING = 'PARSING',
  MAPPING = 'MAPPING',
  FINALIZED = 'FINALIZED',
}

export enum MergeStrategy {
  MERGE = 'MERGE',
  REPLACE = 'REPLACE',
}

export type MappingRelevance = 'FOCUSED' | 'BALANCED' | 'BROAD';

export const MappingRelevance = {
  FOCUSED: 'FOCUSED',
  BALANCED: 'BALANCED',
  BROAD: 'BROAD',
} as const satisfies Record<MappingRelevance, MappingRelevance>;

export interface MappingJob {
  id: string;
  // Null when the viewer doesn't own the job: the upload key is only ever sent
  // to its uploader, since anyone holding it could re-submit the file as their
  // own document (ORA-498). Own jobs still carry it, so retry keeps working.
  s3Key: string | null;
  userId: string;
  examId?: string;
  lectureId?: string;
  reductoJobId?: string;
  status: JobStatus;
  stage: JobStage;
  mappingRelevance: MappingRelevance;
  previewLinkedAt?: string | null;
  startedAt?: string;
  finishedAt?: string;
  parseDurationSec?: number;
  totalDurationSec?: number;
  parsedDocUrl?: string;
  parsedDocJson?: string;
  studioLink?: string;
  numPages?: number;
  creditsUsed?: number;
  aiTitle?: string;
  aiDescription?: string;
  error?: string;
  // Structured failure code from the backend. 'PAGE_LIMIT_EXCEEDED' when the
  // document was rejected for exceeding the per-document page cap (ORA-395).
  errorCode?: string;
  createdAt: string;
  updatedAt: string;
  mergeStrategy?: MergeStrategy;
  fileName?: string;
  // The Content Set the job was mapped against (ORA-376), so a retry preserves it.
  contentSetExamIds?: string[];
  // Document Pipeline v4 (Phase 1a). Absent on V3 jobs and older backends.
  phase?: DocumentModelPhase | null;
  /** 0–100, monotonic. */
  progress?: number | null;
  summaryReadyAt?: string | null;
  mappingReadyAt?: string | null;
  documentModelId?: string | null;
  summaryError?: string | null;
  mappingError?: string | null;
  /** A "Process anyway" rebuild of a document skipped as non-study. */
  forceStudy?: boolean;
  vignettes: Vignette[];
  flashcards: Flashcard[];
  videos: Video[];
}

// Create the entity adapter
// Sort by when the upload started, newest first might be useful
const mappingJobsAdapter = createEntityAdapter<MappingJob>({
  sortComparer: (a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
});

interface MappingJobsState extends ReturnType<
  typeof mappingJobsAdapter.getInitialState
> {
  status: MappingJobsStatus;
  error: string | null;
  selectedMappingJobId: string | null;
}

// Define the initial state
const initialState: MappingJobsState = mappingJobsAdapter.getInitialState({
  status: 'idle',
  error: null,
  selectedMappingJobId: null,
});

export const mappingJobsSlice = createSlice({
  name: 'mappingJobs',
  initialState,
  reducers: {
    mappingJobsLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    mappingJobsReceived: (state, action: PayloadAction<MappingJob[]>) => {
      mappingJobsAdapter.setAll(state, action.payload);
      state.status = 'succeeded';

      // Validate selectedMappingJobId still exists after setting new mapping jobs
      if (state.selectedMappingJobId) {
        const selectedJobExists = state.entities[state.selectedMappingJobId];
        if (!selectedJobExists) {
          state.selectedMappingJobId = null;
        }
      } else {
        // Set to the first mapping job according to the sortComparer order
        if (state.ids.length > 0) {
          state.selectedMappingJobId = state.ids[0] as string;
        }
      }
    },
    mappingJobReceived: (state, action: PayloadAction<MappingJob>) => {
      mappingJobsAdapter.upsertOne(state, action.payload);
      state.status = 'succeeded';
      state.error = null;
    },
    mappingJobDeleted: (state, action: PayloadAction<string>) => {
      const deletedJobId = action.payload;
      mappingJobsAdapter.removeOne(state, deletedJobId);
      state.status = 'succeeded';
      state.error = null;

      // Clear selection if the deleted mapping job was selected
      if (state.selectedMappingJobId === deletedJobId) {
        if (state.ids.length > 0) {
          state.selectedMappingJobId = state.ids[0] as string;
        } else {
          state.selectedMappingJobId = null;
        }
      }
    },
    updateMappingJobStatus: (
      state,
      action: PayloadAction<{
        jobId: string;
        status?: JobStatus;
        stage?: JobStage;
      }>
    ) => {
      const { jobId, status, stage } = action.payload;
      const changes: Partial<MappingJob> = {};

      if (status !== undefined) {
        changes.status = status;
      }

      if (stage !== undefined) {
        changes.stage = stage;
      }

      // Only update if there are actual changes
      if (Object.keys(changes).length > 0) {
        mappingJobsAdapter.updateOne(state, {
          id: jobId,
          changes,
        });
      }
    },
    mappingJobsFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load mapping jobs';
    },
    mappingJobSelected: (state, action: PayloadAction<string>) => {
      // Only set if the mapping job actually exists
      const jobExists = state.entities[action.payload];
      if (jobExists) {
        state.selectedMappingJobId = action.payload;
      }
    },
    mappingJobDeselected: (state) => {
      state.selectedMappingJobId = null;
    },
    clearMappingJobs: (state) => {
      mappingJobsAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      state.selectedMappingJobId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      mappingJobsAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      state.selectedMappingJobId = null;
    });
  },
});

export const {
  mappingJobsLoading,
  mappingJobsReceived,
  mappingJobReceived,
  mappingJobDeleted,
  updateMappingJobStatus,
  mappingJobsFailed,
  mappingJobSelected,
  mappingJobDeselected,
  clearMappingJobs,
} = mappingJobsSlice.actions;

// Export selectors
export const mappingJobsSelectors = mappingJobsAdapter.getSelectors(
  (state: RootState) =>
    state.mappingJobs || mappingJobsAdapter.getInitialState()
);

// Memoized custom selectors
export const selectSelectedMappingJobId = createSelector(
  [(state: RootState) => state.mappingJobs?.selectedMappingJobId],
  (selectedMappingJobId) => selectedMappingJobId
);

export const selectSelectedMappingJob = createSelector(
  [
    selectSelectedMappingJobId,
    (state: RootState) => state.mappingJobs?.entities,
  ],
  (selectedId, entities) => {
    return selectedId && entities ? entities[selectedId] || null : null;
  }
);

export default mappingJobsSlice.reducer;
